import {
	type Collection,
	type CollectionCharacteristics,
	type CollectionDesignImage,
	type CollectionTranslation,
} from '@prisma/client'
import { motion } from 'motion/react'
import { Link } from 'react-router'
import { useIntersectionObserver } from '#app/hooks/use-intersection-observer.ts'
import { cn, getMaterialDesignImgSrc } from '#app/utils/misc.tsx'
import routes from '#routes/index.tsx'
import { Icon } from '../ui/icon'
import FourthDimensionLogo from './fourth-dimension-logo'

export default function ProductJumbo({
	product,
}: {
	product: Collection & {
		translations: CollectionTranslation[]
		designsImages: CollectionDesignImage[]
		characteristics: Pick<
			CollectionCharacteristics,
			'backgroundColor' | 'textColor' | 'fourthDimension'
		>
	}
}) {
	const { isIntersecting: isIntersectingImage, ref: imgRef } =
		useIntersectionObserver({
			threshold: 1,
		})
	return (
		<div className="relative flex h-[170vh] flex-col overflow-visible">
			<div
				id="product-jumbo"
				style={{
					backgroundColor: product.characteristics?.backgroundColor,
					color: product.characteristics?.textColor,
				}}
				className="sticky top-0 z-0 flex h-screen w-full items-center justify-center"
			>
				<div className="mx-auto max-w-[90vw] break-words md:max-w-min">
					<motion.h1
						initial={{
							opacity: 0,
						}}
						animate={{
							opacity: 1,
							transition: {
								duration: 1.5,
								ease: [0.83, 0, 0.17, 1],
								delay: 0.25,
							},
						}}
						className="text-start font-serif text-[3rem] font-light uppercase leading-[0.85] sm:text-[14vw] md:text-[12vw] lg:text-[10vw] xl:text-[8vw]"
					>
						{product.name}
					</motion.h1>
				</div>
			</div>
			{product.designsImages && Array.isArray(product.designsImages) && (
				<div className="relative z-20 mb-[70px] flex h-[300px] flex-1 items-center justify-center">
					<img
						ref={imgRef}
						className="aspect-auto h-full max-h-[490px] 2xl:h-auto 2xl:max-h-none 2xl:w-[10vw]"
						src={getMaterialDesignImgSrc(product.designsImages[0]!.id)}
						alt={product.name}
					/>
					{product.characteristics.fourthDimension && (
						<Link
							viewTransition
							style={{
								opacity: isIntersectingImage ? 1 : 0,
								color: product.characteristics?.textColor,
								border: `1px solid ${product.characteristics?.textColor}`,
							}}
							to={routes.public.landings.fourthDimension.route({})}
							className={cn(
								'group rounded-full border bg-transparent hover:bg-white/10',
								'flex items-center justify-center gap-sm',
								'z-30 transition-all duration-500 ease-in-out',
								'absolute bottom-0 right-1/2 aspect-square w-[200px] translate-x-1/2 sm:w-[300px] md:bottom-[10%] md:left-auto md:right-[12%] md:translate-x-0 lg:w-[350px] 2xl:right-1/4 min-[2000px]:w-[400px]',
							)}
						>
							<div className="flex w-1/3 flex-col items-center justify-start gap-sm text-sm font-bold uppercase tracking-[0.2em]">
								<FourthDimensionLogo
									fill={product.characteristics.textColor}
									className="w-full"
								/>
								<Icon
									name="arrow-right"
									className="mr-auto h-11 w-11 transition-all duration-200 ease-in-out group-hover:ml-md"
								/>
							</div>
						</Link>
					)}
				</div>
			)}
		</div>
	)
}
